import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronDown, FiMenu, FiX } from "react-icons/fi";
import { api } from "../services/api";
import logoImg from "../assets/images/logo.png";
import Loader from "./Loader";
import Footer from "./Footer";

const Home = lazy(() => import("../pages/Home"));
const Vision = lazy(() => import("../pages/Vision"));
const Contact = lazy(() => import("../pages/Contact"));
const Programs = lazy(() => import("../pages/Programs"));
const ProgramDetail = lazy(() => import("../pages/ProgramDetail"));
const Gallery2019 = lazy(() => import("../pages/Gallery2019"));
const Gallery2020 = lazy(() => import("../pages/Gallery2020"));
const Gallery2021 = lazy(() => import("../pages/Gallery2021"));
const Gallery2022 = lazy(() => import("../pages/Gallery2022"));
const Gallery2023 = lazy(() => import("../pages/Gallery2023"));
const Gallery2024 = lazy(() => import("../pages/Gallery2024"));
const Gallery2025 = lazy(() => import("../pages/Gallery2025"));
const MediaCoverage = lazy(() => import("../pages/MediaCoverage"));

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const AnimatedPage = ({ children }) => (
  <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
    {children}
  </motion.div>
);

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [galleryYears, setGalleryYears] = useState([]);
  const [orgName, setOrgName] = useState("PRASOON");
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [navData, galleryData, orgData] = await Promise.all([
          api.getNavItems(),
          api.getGalleryYears(),
          api.getOrganization(),
        ]);
        setNavItems(navData);
        setGalleryYears(galleryData);
        setOrgName(orgData?.name || "PRASOON");
      } catch (error) {
        console.error("Error fetching nav data:", error);
        setNavItems([
          { id: 1, path: "/", label: "Home" },
          { id: 2, path: "/vision", label: "About" },
          { id: 3, path: "/contact", label: "Contact" },
        ]);
        setGalleryYears([
          { id: 1, year: "2025", path: "/gallery25" },
          { id: 2, year: "2024", path: "/gallery24" },
          { id: 3, year: "2023", path: "/gallery23" },
          { id: 4, year: "2022", path: "/gallery22" },
          { id: 5, year: "2021", path: "/gallery21" },
          { id: 6, year: "2020", path: "/gallery20" },
          { id: 7, year: "2019", path: "/gallery19" },
        ]);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsOpen(false);
    setGalleryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setGalleryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isGalleryActive = location.pathname.startsWith("/gallery");

  const handleYearClick = (path) => {
    setGalleryOpen(false);
    setIsOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container-custom">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-2">
              <img src={logoImg} alt={orgName} className="w-12 h-12 rounded-full object-cover" />
              <span className="text-2xl font-bold text-gray-900 font-poppins">{orgName}</span>
            </NavLink>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.filter(item => item.label !== "Contact").map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path) ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <motion.div
                      className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-600 rounded"
                      layoutId="activeTab"
                    />
                  )}
                </NavLink>
              ))}

              {/* Gallery Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setGalleryOpen(!galleryOpen)}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isGalleryActive ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
                  }`}
                >
                  Gallery
                  <FiChevronDown className={`transition-transform duration-200 ${galleryOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {galleryOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 min-w-[160px] bg-white rounded-lg shadow-strong border border-gray-100 py-2 z-50"
                    >
                      {galleryYears.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleYearClick(item.path)}
                          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                            isActive(item.path)
                              ? "bg-primary-50 text-primary-600"
                              : "text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                          }`}
                        >
                          Gallery {item.year}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Contact - Always Last */}
              {navItems.filter(item => item.label === "Contact").map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path) ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <motion.div
                      className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-600 rounded"
                      layoutId="activeTab"
                    />
                  )}
                </NavLink>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden border-t border-gray-200 overflow-hidden"
              >
                <div className="py-4 space-y-1">
                  {navItems.filter(item => item.label !== "Contact").map((item) => (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? "text-primary-600 bg-primary-50"
                          : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.label}
                    </NavLink>
                  ))}

                  {/* Mobile Gallery Dropdown */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setGalleryOpen(!galleryOpen)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isGalleryActive
                          ? "text-primary-600 bg-primary-50"
                          : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      Gallery
                      <FiChevronDown className={`transition-transform duration-200 ${galleryOpen ? "rotate-180" : ""}`} />
                    </button>

                    <AnimatePresence>
                      {galleryOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-4 mt-1 bg-gray-50 rounded-lg overflow-hidden"
                        >
                          {galleryYears.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleYearClick(item.path)}
                              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                                isActive(item.path)
                                  ? "text-primary-600 bg-primary-100"
                                  : "text-gray-600 hover:text-primary-600"
                              }`}
                            >
                              Gallery {item.year}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Contact - Always Last */}
                  {navItems.filter(item => item.label === "Contact").map((item) => (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? "text-primary-600 bg-primary-50"
                          : "text-gray-700 hover:text-primary-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      <AnimatePresence mode="wait">
        <Suspense fallback={<Loader />}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<AnimatedPage><Home /></AnimatedPage>} />
            <Route path="/vision" element={<AnimatedPage><Vision /></AnimatedPage>} />
            <Route path="/programs" element={<AnimatedPage><Programs /></AnimatedPage>} />
            <Route path="/programs/:programId" element={<AnimatedPage><ProgramDetail /></AnimatedPage>} />
            <Route path="/gallery25" element={<AnimatedPage><Gallery2025 /></AnimatedPage>} />
            <Route path="/gallery24" element={<AnimatedPage><Gallery2024 /></AnimatedPage>} />
            <Route path="/gallery23" element={<AnimatedPage><Gallery2023 /></AnimatedPage>} />
            <Route path="/gallery22" element={<AnimatedPage><Gallery2022 /></AnimatedPage>} />
            <Route path="/gallery21" element={<AnimatedPage><Gallery2021 /></AnimatedPage>} />
            <Route path="/gallery20" element={<AnimatedPage><Gallery2020 /></AnimatedPage>} />
            <Route path="/gallery19" element={<AnimatedPage><Gallery2019 /></AnimatedPage>} />
            <Route path="/media-coverage" element={<AnimatedPage><MediaCoverage /></AnimatedPage>} />
            <Route path="/contact" element={<AnimatedPage><Contact /></AnimatedPage>} />
            <Route path="/index.html" element={<AnimatedPage><Home /></AnimatedPage>} />
          </Routes>
        </Suspense>
      </AnimatePresence>

      <Footer />
    </>
  );
}

export default Navbar;
